import apiClient from './client'

export interface SignalRiskAnalysisRequest {
  scriptId?: string;
  dslCode?: string;
  datasetId: string;
  /** Optional: analyze only these signals (1–5). If omitted, backend analyzes all from DSL. */
  signalNames?: string[];
  analysis?: {
    simulationMethod?: 'atr_based' | 'realistic' | 'historical';
    simulationBars?: number;
    stopLossLevels?: number[];
    takeProfitLevels?: number[];
    entryPriceType?: 'close' | 'open' | 'high' | 'low';
    optimizationMetric?: 'win_rate' | 'profit_factor' | 'sharpe_ratio' | 'return';
    signalDirections?: Record<string, 'long' | 'short'>;
    defaultDirection?: 'long' | 'short';
    evaluationMode?: 'hold' | 'day_close' | 'session_close';
    timezone?: string; // Optional timezone for day_close mode (e.g., 'America/New_York')
  };
}

export interface IndividualSignalAnalysis {
  signalName: string;
  signalCount: number;
  direction: 'long' | 'short';
  maeStats: {
    average: number;
    median: number;
    std: number;
    min: number;
    max: number;
    percentiles: {
      25: number;
      50: number;
      75: number;
      90: number;
      95: number;
    };
    distribution: number[];
  };
  mfeStats: {
    average: number;
    median: number;
    std: number;
    min: number;
    max: number;
    percentiles: {
      25: number;
      50: number;
      75: number;
      90: number;
      95: number;
    };
    distribution: number[];
  };
  optimalConfig: {
    stopLoss: number;
    takeProfit: number;
    expectedWinRate: number;
    expectedReturn: number;
    expectedProfitFactor: number;
    expectedMaxDrawdown: number;
    confidenceScore: number;
  };
  metrics: {
    winRate: number;
    profitFactor: number;
    sharpeRatio: number;
    recoveryRatio: number;
    qualityScore: number;
  };
}

export interface SignalRiskAnalysisResponse {
  metadata: {
    totalSignals: number;
    signalsAnalyzed: number;
    simulationBars: number;
    executionTimeMs: number;
    confidence: 'high' | 'medium' | 'low';
    warnings: string[];
  };
  aggregateStats: {
    maeStats: {
      average: number;
      median: number;
      std: number;
      min: number;
      max: number;
      percentiles: {
        25: number;
        50: number;
        75: number;
        90: number;
        95: number;
      };
      distribution: number[];
    };
    mfeStats: {
      average: number;
      median: number;
      std: number;
      min: number;
      max: number;
      percentiles: {
        25: number;
        50: number;
        75: number;
        90: number;
        95: number;
      };
      distribution: number[];
    };
  };
  signalAnalysis: IndividualSignalAnalysis[];
  ranking: {
    bestSignal: {
      name: string;
      reason: string;
      confidence: number;
      metrics: IndividualSignalAnalysis['metrics'];
    };
    signalRanking: Array<{
      rank: number;
      signalName: string;
      score: number;
      reason: string;
    }>;
  };
}

/** Response when analyze-risk creates a job (client polls GET /jobs/:id for result). */
export interface SignalRiskAnalysisJobResponse {
  jobId: string
  status: 'queued'
  bullmqJobId?: string
}

export const signalsApi = {
  /**
   * Start signal risk analysis (creates job). Returns jobId; poll GET /jobs/:id for result.
   */
  analyzeRisk: async (request: SignalRiskAnalysisRequest): Promise<SignalRiskAnalysisJobResponse> => {
    const response = await apiClient.post<SignalRiskAnalysisJobResponse>('/signals/analyze-risk', request)
    return response.data
  }
}